import { ComponentTypes, DyProductSource, IComponent } from 'src/interfaces';

import {
  ComponentItem,
  RowItem,
} from '../modules/CustomerTriggeredEventsTable.interface';

export const handleAddComponentClick = (
  e: React.MouseEvent<HTMLButtonElement>,
  rowId: string,
  rows: RowItem[],
  daypartsCount: number,
  openComponentModal: (rowId: string) => void,
) => {
  e.preventDefault();
  e.stopPropagation();

  const row = rows.find((r) => r.id === rowId);
  if (!row) return;

  const currentComponentCount = row.components?.length || 0;
  if (currentComponentCount >= daypartsCount) {
    return;
  }

  openComponentModal(rowId);
};

export const getSuggestedComponentTypes = (
  componentVariant?: string,
): (DyProductSource | ComponentTypes)[] => {
  const suggestedComponentTypes: (DyProductSource | ComponentTypes)[] = [];
  if (componentVariant === DyProductSource.DYR) {
    suggestedComponentTypes.push(DyProductSource.DYR);
  } else if (componentVariant === DyProductSource.DYS) {
    suggestedComponentTypes.push(DyProductSource.DYS);
  }

  return suggestedComponentTypes;
};

export const handleSwapComponentConfirmed = (
  newComponent: IComponent,
  componentModalRowId: string | null,
  setRows: React.Dispatch<React.SetStateAction<RowItem[]>>,
  handleCloseComponentModal: () => void,
  replaceCompId?: string | null,
) => {
  if (!componentModalRowId) return;

  setRows((prevRows) =>
    prevRows.map((row) => {
      if (row.id !== componentModalRowId) return row;

      let updateComponents = row.components?.map((existingComponent) =>
        existingComponent.id === replaceCompId
          ? {
              ...existingComponent,
              id: newComponent.id.toString(),
              name: newComponent.name,
              theme: newComponent.theme?.name,
              selectedDayparts: existingComponent.selectedDayparts?.map(
                (daypart) => ({
                  ...daypart,
                  component_id: newComponent.id,
                }),
              ),
            }
          : existingComponent,
      );

      return {
        ...row,
        components: updateComponents,
      };
    }),
  );

  handleCloseComponentModal();
};

export const handleAddComponentConfirmed = (
  component: IComponent,
  componentModalRowId: string | null,
  setRows: React.Dispatch<React.SetStateAction<RowItem[]>>,
  handleCloseComponentModal: () => void,
  position: number,
) => {
  if (!componentModalRowId) return;

  setRows((prevRows) =>
    prevRows.map((row) => {
      if (row.id !== componentModalRowId) return row;

      return {
        ...row,
        components: [
          ...(row.components ?? []),
          {
            id: String(component.id),
            name: component.name,
            theme: component.theme?.name,
            selectedDayparts: [],
            position,
          },
        ],
      };
    }),
  );

  handleCloseComponentModal();
};

export const removeDaypartFromComponent = (
  component: ComponentItem,
  daypartId: number,
): ComponentItem => {
  if (component.selectedDayparts && component.selectedDayparts?.length < 0) {
    return component;
  }
  const daypartIds =
    component?.selectedDayparts?.map((daypart) => daypart.daypart_id) ?? [];
  if (!daypartIds.includes(daypartId)) {
    return component;
  }

  let updatedSelectedDayparts = component?.selectedDayparts?.filter(
    (daypart) => daypart.daypart_id !== daypartId,
  );

  return {
    ...component,
    selectedDayparts: updatedSelectedDayparts,
  };
};

export const updateRowComponentsForDaypart = (
  components: ComponentItem[] | undefined,
  compId: string,
  daypartId: number,
  isSelecting: boolean,
): ComponentItem[] => {
  return (components ?? []).map((component) => {
    if (component.id === compId) {
      let updateDayparts = [...(component?.selectedDayparts ?? [])];
      updateDayparts.push({
        id: Math.random(),
        component_id: parseInt(component.id),
        daypart_id: daypartId,
      });

      return {
        ...component,
        selectedDayparts: updateDayparts,
      };
    }

    if (isSelecting) {
      return removeDaypartFromComponent(component, daypartId);
    }

    return component;
  });
};

export const handleDaypartChange = (
  rowId: string,
  compId: string,
  daypartId: number,
  setRows: React.Dispatch<React.SetStateAction<RowItem[]>>,
) => {
  setRows((prevRows) => {
    return prevRows.map((row) => {
      if (row.id !== rowId) return row;

      const clickedComponent = row.components?.find((c) => c.id === compId);
      const currentDaypartIds =
        clickedComponent?.selectedDayparts?.map(
          (daypart) => daypart.daypart_id,
        ) ?? [];
      const isAlreadySelected = currentDaypartIds.includes(daypartId);

      if (isAlreadySelected) {
        return row;
      }

      return {
        ...row,
        components: updateRowComponentsForDaypart(
          row.components,
          compId,
          daypartId,
          true,
        ),
      };
    });
  });
};

export const handleComponentSelect = (
  rowId: string,
  compId: string,
  setRows: React.Dispatch<React.SetStateAction<RowItem[]>>,
) => {
  setRows((prevRows) =>
    prevRows.map((row) =>
      row.id === rowId
        ? {
            ...row,
            components: toggleComponentSelection(row.components, compId),
          }
        : row,
    ),
  );
};

export const toggleComponentSelection = (
  components: ComponentItem[] | undefined,
  compId: string,
): ComponentItem[] => {
  return (components ?? []).map((component) =>
    component.id === compId
      ? { ...component, selected: !component.selected }
      : component,
  );
};

export const isRowValid = (
  row: RowItem,
  expectedDaypartCount: number,
): boolean => {
  const allSelectedDaypartIds = new Set<number>();

  row.components?.forEach((component) => {
    component.selectedDayparts?.forEach((daypart) => {
      allSelectedDaypartIds.add(daypart.daypart_id);
    });
  });

  return allSelectedDaypartIds.size === expectedDaypartCount;
};

export const toggleExpand = (
  id: string,
  setRows: React.Dispatch<React.SetStateAction<RowItem[]>>,
) =>
  setRows((prev) =>
    prev.map((r) => (r.id === id ? { ...r, expanded: !r.expanded } : r)),
  );

export const getComponentOrder = (r: RowItem) =>
  r.components && r.components.length > 0
    ? Math.max(...r.components.map((c) => c.position ?? 0)) + 1
    : 1;
